import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { LogoIcon, LogoResetAnimation } from "@/components/Logo";
import { triggerInstallPrompt, INSTALL_PROMPT_EVENTS } from "@/components/app/InstallPrompt";

const RITUAL_STEPS = [
  { id: "animation", label: "Reset", duration: 3500 }, // ~3.5s breathing animation
  { id: "prompt", label: "Remember", duration: 0 },
  { id: "complete", label: "Continue", duration: 1500 }
];

// Flow steps for the indicator
const FLOW_STEPS = ["Reset", "Breathe", "Remember", "Continue"];

// Check if user prefers reduced motion
const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

export const ResetModal = ({ onComplete, onClose, criticalTasks = [] }) => {
  const [step, setStep] = useState(0);
  const [thought, setThought] = useState("");
  const [reducedMotion] = useState(prefersReducedMotion);
  const textareaRef = useRef(null);

  // Get the most recent critical task that's not in Done
  const activeCriticalTask = criticalTasks.find(t => !t.isDone);

  // Handle animation completion
  useEffect(() => {
    if (step === 0) {
      const duration = reducedMotion ? 2000 : RITUAL_STEPS[0].duration;
      const timer = setTimeout(() => {
        setStep(1);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [step, reducedMotion]);

  // Auto-focus textarea when prompt appears
  useEffect(() => {
    if (step === 1 && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [step]);

  const handleSubmit = () => {
    if (thought.trim()) {
      setStep(2);
      setTimeout(() => {
        onComplete(thought.trim());
        // Trigger install prompt after reset completes
        triggerInstallPrompt(INSTALL_PROMPT_EVENTS.RESET_USED);
      }, RITUAL_STEPS[2].duration);
    }
  };

  const handleUseCritical = () => {
    if (activeCriticalTask) {
      setThought(activeCriticalTask.content);
    }
  };

  // Prevent closing during animation
  const handleBackdropClick = () => {
    if (step > 0) {
      onClose();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 bg-[#FDFBF7]/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md text-center"
      >
        {/* Step 0: Reset Animation - Breathing ritual */}
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              key="animation"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="flex flex-col items-center justify-center space-y-6"
            >
              {reducedMotion ? (
                // Reduced motion: simple soft fade with logo
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                  className="flex items-center justify-center"
                >
                  <LogoIcon size={100} />
                </motion.div>
              ) : (
                // Full animation: breathing rings with logo
                <LogoResetAnimation size={100} />
              )}
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="text-lg text-[#52525B] mb-3"
              >
                Finding your thread…
              </motion.p>
              
              {/* Four-step flow indicator */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="text-xs text-[#D4D4D8] tracking-wide"
              >
                Reset → Breathe → Remember → Continue
              </motion.p>
            </motion.div>
          )}

          {/* Step 1: Prompt */}
          {step === 1 && (
            <motion.div
              key="prompt"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="space-y-6"
            >
              <motion.div 
                className="flex justify-center mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <LogoIcon size={56} glow />
              </motion.div>
              
              <motion.h2 
                className="text-2xl md:text-3xl font-bold text-[#27272A]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15 }}
              >
                What were you just doing?
              </motion.h2>
              
              <motion.p 
                className="text-[#52525B]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                One thought. We'll find your thread.
              </motion.p>

              {/* Critical task suggestion - gentle, non-intrusive */}
              {activeCriticalTask && !thought && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-amber-50/80 border border-amber-200/60 rounded-xl p-4 text-left"
                >
                  <div className="flex items-start gap-3">
                    <span 
                      className="text-amber-400 mt-0.5 flex-shrink-0 text-sm leading-none"
                      style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                    >
                      *
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-amber-600/80 mb-1">
                        You marked this as important earlier
                      </p>
                      <p className="text-sm text-[#27272A] break-words">
                        {activeCriticalTask.content}
                      </p>
                      <button
                        onClick={handleUseCritical}
                        className="mt-2 text-xs text-amber-600 hover:text-amber-700 font-medium transition-colors"
                      >
                        Use this →
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              <motion.div 
                className="relative"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                <textarea
                  ref={textareaRef}
                  value={thought}
                  onChange={(e) => setThought(e.target.value)}
                  placeholder="I was working on..."
                  className="w-full bg-white rounded-2xl p-4 text-lg border-2 border-stone-200 outline-none focus:border-[#F58227] resize-none min-h-[120px] transition-colors"
                />
              </motion.div>
              
              <motion.button
                onClick={handleSubmit}
                disabled={!thought.trim()}
                className="w-full bg-[#F58227] text-white rounded-full py-4 text-lg font-bold hover:bg-[#D96D19] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                Find my thread
              </motion.button>
              
              <motion.button
                onClick={onClose}
                className="text-[#A1A1AA] hover:text-[#52525B] text-sm transition-colors"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35 }}
              >
                Cancel
              </motion.button>
            </motion.div>
          )}

          {/* Step 2: Complete */}
          {step === 2 && (
            <motion.div
              key="complete"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="space-y-6"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
                className="flex justify-center"
              >
                <div className="relative">
                  <LogoIcon size={80} glow />
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3 }}
                    className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#0CBCBF] flex items-center justify-center"
                  >
                    <Check size={18} className="text-white" />
                  </motion.div>
                </div>
              </motion.div>
              <div>
                <p className="font-handwritten text-2xl text-[#F58227] mb-2">
                  "Oh right, that's what I was doing"
                </p>
                <p className="text-[#27272A] text-lg bg-white rounded-xl p-4 border border-stone-100">
                  {thought}
                </p>
              </div>
              <p className="text-[#52525B]">Thread found. Back on track.</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 mt-8">
          {RITUAL_STEPS.map((s, i) => (
            <motion.div
              key={s.id}
              className={`w-2 h-2 rounded-full transition-colors ${
                i <= step ? "bg-[#F58227]" : "bg-stone-300"
              }`}
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: i * 0.1 }}
            />
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};
